import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const subjectOptions = [
  'General Inquiry',
  'ISNetworld help',
  'Veriforce help',
  'Avetta help',
  'RAVS Plus help',
  'PEC Premier help',
  'PIC help',
  'Training help',
  'Website / Software help',
  'Just call me please',
];

export default function ContactServicesOverview() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: 'General Inquiry',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.message) {
      setError('Please fill in all required fields.');
      return;
    }
    if (formData.message.length > 500) {
      setError('Message must be 500 characters or fewer.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      });
      await fetch('https://readdy.ai/api/form/d71uj36uimmp87kg1p2g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Submission failed. Please call 405-408-1117 directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-[#F5F0E8] py-24 px-6 lg:px-14">
      <div className="max-w-6xl mx-auto">
        <p className="overline mb-4">Reach Us</p>
        <h2 className="font-serif text-4xl lg:text-5xl font-light text-[#1A1409] leading-tight gold-line mb-14">
          Get in Touch
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Info column */}
          <div className="lg:col-span-2 flex flex-col gap-8">
            <p className="text-[#5A5A5A] text-sm font-light leading-relaxed">
              Feel free to call us or fill out the form. We work with energy contractors across the US and internationally — no matter where you operate.
            </p>

            <div className="flex items-center gap-4">
              <div className="w-11 h-11 bg-[#C9A030] flex items-center justify-center flex-shrink-0">
                <i className="ri-phone-fill text-[#0A0A0A] text-lg" />
              </div>
              <div>
                <p className="text-[#1A1409]/45 text-[10px] tracking-[0.25em] uppercase font-medium mb-0.5">Phone</p>
                <a
                  href="tel:4054081117"
                  className="text-[#1A1409] font-semibold text-xl tracking-wide cursor-pointer hover:text-[#C9A030] transition-colors"
                >
                  405-408-1117
                </a>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-11 h-11 bg-[#1A1409] flex items-center justify-center flex-shrink-0">
                <i className="ri-map-pin-line text-[#C9A030] text-lg" />
              </div>
              <div>
                <p className="text-[#1A1409]/45 text-[10px] tracking-[0.25em] uppercase font-medium mb-0.5">Based In</p>
                <p className="text-[#1A1409] font-medium text-base">Oklahoma City, OK</p>
                <p className="text-[#5A5A5A] text-xs font-light mt-0.5">Serving contractors worldwide</p>
              </div>
            </div>

            <div>
              <p className="text-[#1A1409]/45 text-[10px] tracking-[0.25em] uppercase font-medium mb-4">Platforms We Manage</p>
              <div className="flex flex-col gap-4">
                <img src="https://www.energycontractorservices.com/wp-content/uploads/2013/04/Screen-shot-2013-05-30-at-10.38.08-AM.png" alt="ISNetworld" className="h-8 w-auto object-contain opacity-60" />
                <img src="https://www.energycontractorservices.com/wp-content/uploads/2024/06/veri-2.png" alt="Veriforce" className="h-9 w-auto object-contain opacity-60" />
                <img src="https://www.energycontractorservices.com/wp-content/uploads/2012/06/avetta-logo-white-768x265-300x104.png" alt="Avetta" className="h-7 w-auto object-contain opacity-60" />
              </div>
            </div>
          </div>

          {/* Form column */}
          <div
            ref={ref}
            className={`lg:col-span-3 fade-right ${isVisible ? 'visible' : ''}`}
          >
            {submitted ? (
              <div className="bg-[#0A0A0A] p-12 flex flex-col items-center justify-center text-center min-h-96 border border-[#C9A030]/15">
                <div className="w-12 h-12 flex items-center justify-center bg-[#C9A030] mb-6">
                  <i className="ri-check-line text-[#0A0A0A] text-2xl" />
                </div>
                <h3 className="font-serif text-2xl font-light text-[#F2EDE3] mb-3">Message Received</h3>
                <p className="text-[#9A9A9A] text-sm font-light leading-relaxed max-w-xs">
                  Thank you. A dedicated ECS expert will be in touch within one business day. Or call us directly at{' '}
                  <a href="tel:4054081117" className="text-[#C9A030] cursor-pointer">405-408-1117</a>.
                </p>
              </div>
            ) : (
              <form
                id="ecs-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-[#0A0A0A] p-8 lg:p-10 border border-[#C9A030]/12"
              >
                <h3 className="font-serif text-xl font-light text-[#F2EDE3] mb-7">Send Us a Message</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-[#9A9A9A] text-[10px] tracking-[0.25em] uppercase mb-2 font-medium">
                      First Name <span className="text-[#C9A030]">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full bg-[#111111] border border-[#F2EDE3]/08 text-[#F2EDE3] text-sm px-4 py-3 outline-none focus:border-[#C9A030]/50 transition-colors placeholder-[#4A4A4A]"
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label className="block text-[#9A9A9A] text-[10px] tracking-[0.25em] uppercase mb-2 font-medium">
                      Last Name <span className="text-[#C9A030]">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full bg-[#111111] border border-[#F2EDE3]/08 text-[#F2EDE3] text-sm px-4 py-3 outline-none focus:border-[#C9A030]/50 transition-colors placeholder-[#4A4A4A]"
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-[#9A9A9A] text-[10px] tracking-[0.25em] uppercase mb-2 font-medium">
                    Email <span className="text-[#C9A030]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-[#111111] border border-[#F2EDE3]/08 text-[#F2EDE3] text-sm px-4 py-3 outline-none focus:border-[#C9A030]/50 transition-colors placeholder-[#4A4A4A]"
                    placeholder="your@email.com"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-[#9A9A9A] text-[10px] tracking-[0.25em] uppercase mb-2 font-medium">
                    Subject <span className="text-[#C9A030]">*</span>
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full bg-[#111111] border border-[#F2EDE3]/08 text-[#F2EDE3] text-sm px-4 py-3 outline-none focus:border-[#C9A030]/50 transition-colors cursor-pointer"
                  >
                    {subjectOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-5">
                  <label className="block text-[#9A9A9A] text-[10px] tracking-[0.25em] uppercase mb-2 font-medium">
                    Message <span className="text-[#C9A030]">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    rows={5}
                    className="w-full bg-[#111111] border border-[#F2EDE3]/08 text-[#F2EDE3] text-sm px-4 py-3 outline-none focus:border-[#C9A030]/50 transition-colors resize-none placeholder-[#4A4A4A]"
                    placeholder="Tell us about your compliance needs..."
                  />
                  <p className="text-[#4A4A4A] text-xs mt-1 text-right">{formData.message.length}/500</p>
                </div>

                {error && <p className="text-red-400 text-xs mb-4 font-light">{error}</p>}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#C9A030] text-[#0A0A0A] py-4 text-[11px] font-bold tracking-[0.25em] uppercase cursor-pointer transition-colors duration-200 hover:bg-[#D4B045] disabled:opacity-60 whitespace-nowrap"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>

                <p className="text-[#4A4A4A] text-xs font-light mt-4 text-center">
                  Or call us directly at{' '}
                  <a href="tel:4054081117" className="text-[#C9A030] cursor-pointer hover:text-[#D4B045]">
                    405-408-1117
                  </a>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
